import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  GoogleMap,
  Marker,
  useLoadScript,
  InfoWindow,
  MarkerClusterer,
} from "@react-google-maps/api";
import AxiosInstance from "../utils/axiosInstance";
import loader from "../assets/loader.svg";
import CountryFilter from "./Filters/CountryFilter";
import WorkTypeFilter from "./Filters/WorkTypeFilter";
import { useNavigate } from "react-router-dom";
import searchIcon from "../assets/search-icon.svg";

const mapContainerStyle = {
  width: "100%",
  height: "80vh",
};

function Map({ EndPoint }) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_MAP_ACCESS_TOKEN,
  });

  const navigate = useNavigate();
  const [rawMarkers, setRawMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState(
    JSON.parse(localStorage.getItem("selectedState")) || null
  );
  const [workTypeId, setWorkTypeId] = useState("");
  const [county, setCounty] = useState("");
  const [resetTrue, setResetTrue] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);

  const hoverTimeout = useRef(null);

  // ✅ Cache to store API responses per filter key
  const cacheRef = useRef({});

  const handleCountrySelect = (countryCode) => {
    setCounty(countryCode);
    setResetTrue(false);
  };

  const handleWorkTypeSelect = (workType) => {
    setWorkTypeId(workType);
    setResetTrue(false);
  };

  const handleSearchClick = () => {
    console.log("Search for:", searchValue);
    fetchMarkers();
  };

  const handleReset = () => {
    setResetTrue(true);
    setWorkTypeId("");
    setCounty("");
    setSearchValue("");
    fetchMarkers();
  };

  const fetchMarkers = async () => {
    setLoading(true);
    if (!selectedState?.label) return;

    if (!EndPoint) {
      setRawMarkers([]);
      setTimeout(() => setLoading(false), 800);
      return;
    }

    // ✅ Include searchValue in cache key
    const cacheKey = JSON.stringify({
      report_id: workTypeId,
      stateid: selectedState.label,
      county,
      search_text: searchValue, // <-- added
    });

    // ✅ Return cached data if exists
    if (cacheRef.current[cacheKey]) {
      setRawMarkers(cacheRef.current[cacheKey]);
      setTotalRecords(cacheRef.current[cacheKey].length);
      setLoading(false);
      return;
    }

    const queryParams = new URLSearchParams({
      report_id: workTypeId || "",
      stateid: selectedState.label || "",
      county: county || "",
      search_text: searchValue || "", // <-- send search text to API
    });

    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_API_URL}${EndPoint}?${queryParams}`
      );
      const fetchedData = response.data?.data || [];

      // ✅ Save to cache including search_text
      cacheRef.current[cacheKey] = fetchedData;

      setRawMarkers(fetchedData);
      setTotalRecords(fetchedData.length);
    } catch (error) {
      console.error("Error fetching markers:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchMarkers();
  }, [county, workTypeId, EndPoint]);

  // Memoize markers
  const markers = useMemo(() => {
    return (
      rawMarkers
        ?.filter((item) => item.lat && item.lon)
        .map((item) => ({
          id: item.account_id,
          position: {
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon),
          },
          title: item.account_name,
          other: {
            address:
              item.billing_address_street +
              " ," +
              item.billing_address_city +
              " , " +
              item.billing_address_state +
              " , " +
              item.zip_code || "N/A",
            county: item.billing_address_city || "N/A",
            phone: item.phone_office || "N/A",
            certificates: item.certificate_names || "N/A",
          },
        })) || []
    );
  }, [rawMarkers]);

  // Memoize center
  const center = useMemo(() => {
    if (markers.length === 0) return { lat: 32.8065, lng: -86.7911 };
    const avgLat =
      markers.reduce((sum, m) => sum + m.position.lat, 0) / markers.length;
    const avgLng =
      markers.reduce((sum, m) => sum + m.position.lng, 0) / markers.length;
    return { lat: avgLat, lng: avgLng };
  }, [markers]);

  return (
    <div className="container">
      {/* ===== Filter Row ===== */}
      <div className="row">
        <div className="col">
          <div className="card w-100 mt-n20 bg-white rounded-3 shadow-sm mb-5">
            <div className="card-body p-3">
              <div className="row g-3 align-items-center">
                <div className="col-12 col-md-auto">
                  <span className="fw-bold text-uppercase text-secondary small me-2">
                    Filter:
                  </span>
                </div>

                <div className="col-12 col-md">
                  <WorkTypeFilter
                    onSelectWorkType={handleWorkTypeSelect}
                    reset={resetTrue}
                  />
                </div>

                <div className="col-12 col-md">
                  <CountryFilter
                    onSelectCountry={handleCountrySelect}
                    reset={resetTrue}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control bg-light border border-secondary text-dark fw-semibold"
                      placeholder="Search..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                    />
                    <button
                      className="btn btn-primary text-white"
                      type="button"
                      onClick={handleSearchClick}
                    >
                      <img
                        src={searchIcon}
                        alt="search"
                        style={{ width: "18px", height: "18px" }}
                      />
                    </button>
                  </div>
                </div>

                <div className="col-6 col-md-auto text-md-end">
                  <button
                    className="btn btn-outline-danger btn-sm text-uppercase"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                </div>
                <div className="col-6 col-md-auto text-md-end">
                  <span className="text-muted fw-semibold small">
                    Total: <span className="text-dark">{totalRecords}</span>
                  </span>
                </div>

                <div
                  className="bg-white rounded shadow-sm border mt-3"
                  style={{ overflow: "hidden" }}
                >
                  {isLoaded ? (
                    loading ? (
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <img src={loader} alt="loading" />
                      </div>
                    ) : (
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        zoom={5}
                        center={center}
                        maxZoom={50}
                        onClick={() => setSelectedMarker(null)}
                      >
                        <MarkerClusterer
                          gridSize={80}
                          maxZoom={14}
                          disableClusteringAtZoom={14}
                          minimumClusterSize={5}
                          options={{
                            styles: [
                              {
                                url: "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m1.png",
                                width: 50,
                                height: 50,
                                textColor: "#ffffff",
                                textSize: 12,
                                anchorText: [-3, 0],
                              },
                            ],
                          }}
                        >

                          {(clusterer) =>
                            markers.map((marker) => (
                              <Marker
                                key={marker.id}
                                position={marker.position}
                                title={marker.title}
                                clusterer={clusterer}
                                icon={{
                                  url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                                  scaledSize: new window.google.maps.Size(40, 40),
                                }}
                                onClick={() => setSelectedMarker(marker)}
                              />
                            ))
                          }
                        </MarkerClusterer>

                        {selectedMarker && (
                          <InfoWindow
                            position={{
                              lat: selectedMarker.position.lat + 0.002,
                              lng: selectedMarker.position.lng,
                            }}
                            onCloseClick={() => setSelectedMarker(null)}
                          >
                            <div className="info-window">
                              <h5 className="fw-bold text-primary mb-2">
                                {selectedMarker.title}
                              </h5>
                              <p className="mb-1">
                                <strong>Address:</strong>{" "}
                                {selectedMarker.other.address}
                              </p>
                              <p className="mb-1">
                                <strong>County:</strong>{" "}
                                {selectedMarker.other.county}
                              </p>
                              <p className="mb-1">
                                <strong>Phone:</strong>{" "}
                                {selectedMarker.other.phone}
                              </p>
                              <p className="mb-1 fw-semibold">Certificates:</p>
                              <ul className="mb-0 ps-3">
                                {selectedMarker.other.certificates
                                  .split("#")
                                  .map((cert, index) => (
                                    <li key={index}>{cert.trim()}</li>
                                  ))}
                              </ul>
                            </div>
                          </InfoWindow>
                        )}
                      </GoogleMap>
                    )
                  ) : (
                    <div className="d-flex justify-content-center align-items-center h-100">
                      <img src={loader} alt="loading" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>
        {`
          .info-window {
            width: 280px;
            background: #fff;
            padding: 10px 14px;
            border-radius: 10px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            line-height: 1.5;
          }
          .info-window ul {
            list-style-type: none;
            padding-left: 0;
          }
          .info-window li::before {
            content: "• ";
            color: #007bff;
          }
        `}
      </style>
    </div>
  );
}

export default Map;
